import { IconPlus } from '@developerz/ui';
import { A } from '@solidjs/router';
import { createSignal, type JSX, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { DonateWizard } from '../components/DonateWizard';
import { DonationsTable } from '../components/DonationsTable';
import { EmptyState } from '../components/EmptyState';
import { FinishDonationBanner } from '../components/FinishDonationBanner';
import { LoadMore } from '../components/LoadMore';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Toast } from '../components/Toast';
import { Button, buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import { unfinishedProjects } from '../lib/donateWizard';
import {
  DONATIONS_POLL_MS,
  type DonationRow,
  detachDonation,
  fetchDonationsPage,
  useDonations,
} from '../lib/donations';
import { createKeyedMutation } from '../lib/keyed-mutation';
import { orgHref } from '../lib/org';
import { usePagedQuery } from '../lib/paged';
import { SESSION_TOKEN } from '../lib/session';

// Donations the caller GAVE (arc 101 slice 11, plan doc 05) — the donor half of
// `/v1/donations`. Its sibling, the maintainer's inbox, is the OTHER side of the
// same gift and is reached from here rather than from a second rail entry: one
// person is usually both, and two nav rows for one noun reads like two features.
//
// Container. Three rules it holds that nothing below it does:
//
//   - **Every write is followed by a re-READ.** A gift's state is the row, never
//     a POST echo: a `money` gift comes back `active: false` and only Stripe's
//     webhook flips it. So a detach calls `reload()` rather than patching a row.
//   - **The wizard is opened, not routed to.** Resuming an unfinished donation
//     hands it the project name, so a donor who paid and attached nothing lands
//     on the step they stopped at instead of retyping the slug.
//   - **Parked and pending gifts stay listed.** Capacity vanishing silently is
//     the failure this page exists to prevent.

export function Donations(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const gifts = usePagedQuery<DonationRow>((cursor) => useDonations(cursor, token), {
    poll: { every: () => DONATIONS_POLL_MS, read: (cursor) => fetchDonationsPage(cursor, token) },
  });
  const [wizard, setWizard] = createSignal(false);
  const [resume, setResume] = createSignal<string | null>(null);
  // Busy is keyed BY GIFT, the `createDonationInbox` rule from the other side of
  // this noun: two detaches are two independent writes, and a single "which row
  // is detaching" slot re-enables row A the moment row B starts — then whichever
  // request settles first clears the other's spinner too. `createKeyedMutation`
  // is that lock; `createMutation`, single-flight per SURFACE, is exactly what a
  // per-row control must not have.
  const detach = createKeyedMutation({ fallback: t('donations.detach_error') });
  const hasSession = token !== null;

  const detachRow = (row: DonationRow): void => {
    void detach.run(row.id, async () => {
      await detachDonation(row.id, token);
      gifts.reload();
    });
  };

  const openWizard = (project: string | null): void => {
    setResume(project);
    setWizard(true);
  };

  return (
    <section class="page donations">
      <PageHeader
        title={t('donations.title')}
        desc={t('donations.subtitle')}
        actions={
          <>
            <A
              class={buttonClass({ variant: 'outline', size: 'sm' })}
              href={orgHref('/donations/received')}
            >
              {t('donations.received_link')}
            </A>
            {/* The wizard lives inside the signed-in branch, so a signed-out
                visitor pressing this would set state nothing renders. */}
            <Show when={hasSession}>
              <Button size="sm" onClick={() => openWizard(null)}>
                <IconPlus />
                {t('donations.donate_cta')}
              </Button>
            </Show>
          </>
        }
      />

      <Show when={hasSession} fallback={<EmptyState message={t('donations.no_session')} />}>
        <FinishDonationBanner projects={unfinishedProjects(gifts.rows())} onResume={openWizard} />

        <Panel title={t('donations.gifts_heading')}>
          <QueryBoundary
            query={gifts.view}
            isEmpty={(rows) => rows.length === 0}
            loading={<CardGridSkeleton listClass="grid-cards-3" />}
            empty={
              <EmptyState
                message={t('donations.empty')}
                ctaLabel={t('donations.donate_cta')}
                onCta={() => openWizard(null)}
              />
            }
          >
            {(rows) => (
              <>
                <DonationsTable rows={rows} isDetaching={detach.isBusy} onDetach={detachRow} />
                <LoadMore list={gifts} />
              </>
            )}
          </QueryBoundary>
        </Panel>

        <DonateWizard
          open={wizard()}
          token={token}
          rows={gifts.rows()}
          resumeProject={resume()}
          onClose={() => setWizard(false)}
          onChanged={gifts.reload}
        />
        <Toast message={detach.error()} tone="error" onDismiss={detach.clearError} />
      </Show>
    </section>
  );
}
